<template>
  <div id="app__ernest">
    <header class="app__header__ernest">
      <div>
        <u-icon>æ</u-icon>
      </div>
      <div>
        <div><span>{{ $t('app.ernestHeader.repository') }}</span></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
      <div></div>
    </header>

    <section class="app__section__ernest">
      <div id="treeview">
        <v-progress-circular
          indeterminate
          color="valid"
          v-if="progressCircular.display"
        />

        <div v-else>
          <v-treeview
            :items="repositoryTree"
            :search="search"
            :open-on-click="true"
            :open="open"
            item-key="name"
          >
            <template slot="prepend" slot-scope="{ item }">
              <v-badge left
                       :color="getWeightColor(item.type)"
                       v-if="hasQuestionnaireSelected && (item.type === 'domain' || item.type === 'skill' || item.type === 'subSkill')"
              >
                <template v-slot:badge>
                  <span class="question__badge__weight"
                        :title="$t('pages.repository.Detail.tooltip.skillWeight')"
                  >{{ item.totalWeight }}</span>
                </template>
              </v-badge>
            </template>

            <template slot="append" slot-scope="{ item }">
              <v-btn :title="$t('pages.repository.Detail.tooltip.questionCount')"
                     :color="getQuestionCountColor(item.questionCount)"
                     small
                     fab
                     @click.stop="questionOpenModalList(item)"
                     v-if="(item.type === 'skill' || item.type === 'subSkill')"
              >{{item.questionCount}}
              </v-btn>
            </template>

            <template slot="label" slot-scope="{ item }">
              <ul v-if="hasQuestionnaireSelected">
                <li>{{ item.name }}</li>
                <li v-for="question in item.questions" :key="question.id"
                    class="question__weight__item">
                  <span :title="$t('pages.repository.Detail.tooltip.questionWeight')"
                        class="question-weight">
                    {{ question.weight }} -&nbsp;
                  </span>
                  {{ question.label }}
                </li>
              </ul>
              <span v-else>{{ item.name }}</span>
            </template>
          </v-treeview>
        </div>
      </div>

      <!-- Modal qui affiche les questions associées-->
      <v-dialog
        v-model="modalQuestionList"
        content-class="v-dialog__ernest"
      >
        <div class="card__container__skill">
          <header>
            <v-btn small absolute right text tile color="error"
                   @click.stop="modalQuestionList=false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <h1>{{ $t('pages.repository.Detail.questionList.title')}}</h1>
          </header>

          <div class="card__content__treeview">
            <v-data-table :headers="headersQuestionRelated"
                          :items="selectedQuestions"
                          :loading="loadingQuestionList"
                          hide-default-footer
            >
              <template v-slot:progress>
                <v-progress-linear height="2" slot="progress" color="blue" indeterminate/>
              </template>
              <template v-slot:no-data>
                {{ $t('pages.repository.Detail.questionList.noQuestion')}}
              </template>
              <template v-slot:body="{items}">
                <tbody>
                <tr v-for="question in items" :key="question.id">
                  <td>{{ question.label }}</td>
                  <td>
                    <v-text-field v-model="question.weight"
                                  type="number"
                                  min="1" max="5"
                                  :error-messages="weightError(question.weight)"
                    />
                  </td>
                  <td>
                    <v-btn small
                           color="error"
                           fab
                           @click.stop="deleteQuestion(question.id)"
                    >
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </td>
                </tr>
                </tbody>
              </template>
            </v-data-table>
          </div>
          <footer>
            <v-btn color="other1" @click.stop="questionOpenModalLikert">
              {{ $t('pages.repository.Detail.questionList.addQuestion')}}
            </v-btn>
            <v-btn color="valid" @click.stop="onSaveQuestionList" :disabled="saveDisabled">
              {{ $t('components.buttons.save')}}
            </v-btn>
            <v-btn color="cancel" @click.stop="modalQuestionList=false">
              {{ $t('components.buttons.cancel')}}
            </v-btn>
          </footer>
        </div>
      </v-dialog>

      <!-- Modal qui affiche le tableau des questions de Likert-->
      <v-dialog
        v-model="modalQuestionLikert"
        content-class="v-dialog__ernest"
      >
        <div class="card__container__skill">
          <header>
            <v-btn small absolute right text tile color="error"
                   @click.stop="modalQuestionLikert=false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <h1>{{ $t('pages.repository.Detail.likertList.title')}}</h1>
          </header>
          <div class="card__content__treeview">
            <v-data-table :headers="headersQuestionLikert"
                          :items="questions"
                          :loading="loadingQuestionLikert"
            >
              <v-progress-linear height="2" slot="progress" color="blue" indeterminate/>
              <template v-slot:body="{ items }">
                <tbody>
                <tr v-for="question in items" :key="question.id">
                  <td>
                    <v-checkbox v-model="question.selected"/>
                  </td>
                  <td>{{ question.label }}</td>
                  <td>{{ question.text }}</td>
                </tr>
                </tbody>
              </template>
            </v-data-table>
          </div>
          <footer>
            <v-btn color="valid" @click.stop="onSaveQuestionLikert">
              {{ $t('components.buttons.save')}}
            </v-btn>
            <v-btn color="cancel" @click.stop="modalQuestionLikert=false">
              {{ $t('components.buttons.cancel')}}
            </v-btn>
          </footer>
        </div>
      </v-dialog>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DEFAULT_WEIGHT } from '@/store/questions'

const badgeColors = {
  domain: 'hsl(174, 75%, 20%)',
  skill: 'hsl(174, 80%, 30%)',
  subSkill: 'hsl(174, 75%, 40%)'
}

export default {
  name: 'RepositoryDetail',
  data () {
    return {
      progressCircular: {
        display: true
      },
      search: '',
      repositoryTree: [],
      selectedSkill: {},
      selectedQuestions: [],
      questions: [],
      open: [],
      loadingQuestionLikert: true,
      loadingQuestionList: true,
      modalQuestionLikert: false,
      modalQuestionList: false,
      headersQuestionRelated: [
        {
          text: this.$t('pages.repository.Detail.questionList.label'),
          align: 'left',
          value: 'label'
        },
        {
          text: this.$t('pages.repository.Detail.questionList.weight'),
          align: 'center',
          value: 'weight'
        },
        {
          text: '',
          align: 'center',
          value: 'actions'
        }
      ],
      headersQuestionLikert: [
        {
          text: '',
          align: 'left',
          sortable: false,
          value: 'actions'
        },
        {
          text: this.$t('pages.repository.Detail.likertList.label'),
          align: 'left',
          value: 'label'
        },
        {
          text: this.$t('pages.repository.Detail.likertList.text'),
          align: 'left',
          value: 'text'
        }
      ]
    }
  },
  computed: {
    ...mapGetters('Repositories', [
      'questionnaireFilterId',
      'repositorySelected'
    ]),
    ...mapGetters('Questions', [
      'questionsLikert'
    ]),
    ...mapGetters('Questionnaires', [
      'questionnaires'
    ]),
    repositoryName () {
      return this.repositorySelected ? this.repositorySelected.name : ''
    },
    hasQuestionnaireSelected () {
      return !!this.questionnaireFilterId
    },
    saveDisabled () {
      return this.selectedQuestions.some(question => {
        this.isNoWeight(question.weight)
      })
    }
  },
  watch: {
    async questionnaireFilterId (newQuestionnaireId) {
      this.progressCircular.display = true
      await this.loadRepository(newQuestionnaireId)
      this.repositoryTree = [this.repositorySelected]
      this.progressCircular.display = false
    }
  },
  async created () {
    await this.loadRepository(this.hasQuestionnaireSelected ? this.questionnaireFilterId : false)
    this.repositoryTree = [JSON.parse(JSON.stringify(this.repositorySelected))]
    this.open = [this.repositorySelected.name]

    await this.loadQuestionnaires()
    this.progressCircular.display = false
  },
  methods: {
    ...mapActions('Repositories', [
      'loadRepository',
      'loadFilteredRepository',
      'saveSkill'
    ]),
    ...mapActions('Questions', [
      'loadActiveLikerts',
      'loadLikertsOfQuestionnaire'
    ]),
    ...mapActions('Questionnaires', [
      'loadQuestionnaires'
    ]),
    deleteQuestion (id) {
      this.selectedQuestions = this.selectedQuestions.filter(question => question.id !== id)
    },
    async questionOpenModalList (skill) {
      this.modalQuestionList = true
      this.selectedSkill = { ...skill }
      this.selectedQuestions = skill.questions.map(q => ({ ...q }))
      this.loadingQuestionList = false
    },
    async questionOpenModalLikert () {
      this.modalQuestionLikert = true
      if (this.hasQuestionnaireSelected) {
        await this.loadLikertsOfQuestionnaire(this.questionnaireFilterId)
      } else {
        await this.loadActiveLikerts()
      }
      this.questions = this.questionsLikert
        .map(question => ({
          id: question.id,
          label: question.label,
          text: question.text,
          weight: question.skills
            .filter(skill => skill.id === this.selectedSkill.id)
            .reduce((weight, skill) => skill.weight, DEFAULT_WEIGHT),
          selected: this.selectedQuestions.map(q => q.id)
            .includes(question.id)
        }))
      this.loadingQuestionLikert = false
    },
    onSaveQuestionLikert () {
      const preSelectedids = this.selectedQuestions.map(q => q.id)
      this.selectedQuestions = this.questions
        .filter(q => q.selected)
        .map(question => {
          return preSelectedids.includes(question.id)
            ? this.selectedQuestions.find(sq => sq.id === question.id)
            : {
              id: question.id,
              label: question.label,
              weight: question.weight
            }
        })
      this.modalQuestionLikert = false
    },
    async onSaveQuestionList () {
      await this.saveSkill({
        id: this.selectedSkill.id,
        newQuestions: this.selectedQuestions.map(q => ({
          ...q,
          weight: parseInt(q.weight)
        }))
      })
      this.repositoryTree = [this.repositorySelected]
      this.modalQuestionList = false
    },
    getQuestionCountColor (questionCount) {
      const base = Math.max(1, this.repositorySelected.maxQuestionCount)
      const luminosity = Math.floor(95 - 40 * questionCount / base)
      return `hsl(183, 57%, ${luminosity}%)`
    },
    getWeightColor (itemType) {
      return badgeColors[itemType]
    },
    isNoWeight (weight) {
      const w = parseFloat(weight)
      return isNaN(w) || !Number.isInteger(w) || w > 5 || w < 1
    },
    weightError (weight) {
      return this.isNoWeight(weight)
        ? this.$t('pages.repository.Detail.questionList.weightError')
        : ''
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../../assets/style/styleAppErnest";
  @import "../../assets/style/styleAppDialogScopedErnest";

  #app__ernest {
    min-width: 0;

    .v-treeview-node {
      margin-bottom: 10px;
    }
  }

  .v-badge {
    margin-left: 30px;

    .question__badge__weight {
      color: white;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    .question__weight__item {
      margin-left: 15px;

      .question-weight {
        color: hsl(174, 80%, 30%);
      }
    }
  }

  .app__treeview__branch {
    height: 65px;
    display: flex;
    align-items: center;

    .v-btn {
      margin-left: 20px;
    }
  }
</style>

<style lang="scss">
  @import "../../assets/style/styleAppDialogErnest";
</style>
